import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { loadCredits, loadCreditsLedger } from "../../../creditsApi";
import { formatRelativeTime } from "../../../utils";
import type { CreditsLedgerEntry } from "../../../types";

// Streak bonus days - mirrors server.js's STREAK_BONUSES keys exactly (kept
// in sync by hand, same as every other client-side mirror of a server
// constant in this app - e.g. SendGiftDialog.tsx's MIN_LOCK_LEAD_MINUTES
// vs the server's own technical floor). Only used to preview "how many
// days until the next milestone," not to compute any actual reward amount
// - the server is the sole source of truth for that.
const STREAK_MILESTONE_DAYS = [3, 7, 10];

function nextMilestone(currentStreak: number): number | null {
  return STREAK_MILESTONE_DAYS.find((d) => d > currentStreak) ?? null;
}

function reasonLabel(reason: string): string {
  if (reason === "DAILY_LOGIN") return "Daily reward";
  if (reason.startsWith("STREAK_")) return `${reason.replace("STREAK_", "").replace("_DAY", "")}-day streak bonus`;
  if (reason === "GIFT_PURCHASE") return "Sent a gift";
  if (reason === "GIFT_PURCHASE_REFUND") return "Refund";
  return reason;
}

function LedgerSkeleton() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <div className="rewards-ledger-row-skeleton skeleton-card" key={i} />
      ))}
    </>
  );
}

// Dedicated "/rewards" page - reached by tapping the credit balance
// wherever it's shown (SendGiftScreen.tsx today). Kept deliberately lean
// for V1 (spec's own "do not overcomplicate" allowance) - balance, streak,
// next milestone preview, recent activity from the ledger. No day-by-day
// streak calendar grid; the backend (GET /api/credits, GET /api/credits/
// ledger) already supports building one later without changes here.
export function RewardsScreen() {
  const navigate = useNavigate();
  const balance = useAppState((s) => s.credits);
  const streak = useAppState((s) => s.creditsStreak);
  const [ledger, setLedger] = useState<CreditsLedgerEntry[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    loadCredits();
    loadCreditsLedger()
      .then(setLedger)
      .finally(() => setLoaded(true));
  }, []);

  const container = getVanillaEl<HTMLDivElement>("rewardsView");
  if (!container) return null;

  const next = nextMilestone(streak.currentStreak);

  return createPortal(
    <>
      <div className="rewards-page-header">
        <button type="button" className="rewards-page-back" aria-label="Back" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left" aria-hidden="true" />
        </button>
        <span className="rewards-page-title">Rewards</span>
      </div>

      <div className="rewards-balance-card">
        <span className="rewards-balance-amount">
          <i className="bi bi-gem" aria-hidden="true" /> {balance}
        </span>
        <span className="rewards-balance-label">credits</span>
      </div>

      <div className="rewards-streak-row">
        <div className="rewards-streak-stat">
          <span className="rewards-streak-stat-value">
            <i className="bi bi-fire" aria-hidden="true" /> {streak.currentStreak}
          </span>
          <span className="rewards-streak-stat-label">day streak</span>
        </div>
        <div className="rewards-streak-stat">
          <span className="rewards-streak-stat-value">{streak.longestStreak}</span>
          <span className="rewards-streak-stat-label">longest streak</span>
        </div>
      </div>

      {next !== null && (
        <div className="rewards-next-card">
          <span className="rewards-next-label">Next milestone</span>
          <span className="rewards-next-value">
            Day {next} · {next - streak.currentStreak} {next - streak.currentStreak === 1 ? "day" : "days"} away
          </span>
        </div>
      )}

      <div className="gifts-section-label">Recent Activity</div>
      <div className="rewards-ledger">
        {!loaded ? (
          <LedgerSkeleton />
        ) : !ledger.length ? (
          <p className="small muted">No activity yet - come back tomorrow for your next daily reward.</p>
        ) : (
          ledger.map((entry) => (
            <div className="rewards-ledger-row" key={entry.id}>
              <span className={`rewards-ledger-amount${entry.amount >= 0 ? " is-credit" : " is-debit"}`}>
                {entry.amount >= 0 ? "+" : ""}
                {entry.amount}
              </span>
              <span className="rewards-ledger-reason">{reasonLabel(entry.reason)}</span>
              <span className="rewards-ledger-time">{formatRelativeTime(entry.createdAt)}</span>
            </div>
          ))
        )}
      </div>
    </>,
    container,
  );
}
